import { Component, Inject, OnInit } from '@angular/core';
import {
    CMPS_DIALOG_DATA,
    CmpsDialog,
    CmpsDialogRef,
    CmpsSnackBar,
    CmpsTextOnlySnackBarComponent,
    CmpsTextOnlySnackBarOptions,
} from '@cmps/ng-uikit';
import { TranslateService } from '@ngx-translate/core';
import { skip, tap } from 'rxjs/operators';

import { CoachingSwitchboardService } from '../../coaching-base/switchboard';
import {
    takeUntilDestroy,
    TakeUntilDestroy,
} from '../../common/take-until-destroy.decorator';
import { useDispatcher } from '../../store.decorator';
import { useSelector2 } from '../store-use-selector';

import { View } from './enum/view.enum';
import { TranscriptDialogData } from './model/transcript-dialog-data.model';
import { reload } from './state/reload.action';
import { reset } from './state/reset.action';
import { setNoteViaSummary } from './state/set-note-via-summary.action';
import { switchView } from './state/switch-view.action';
import { updateTranscript } from './state/update-transcript.action';
import { TranslationKeyMap } from './transcript-dialog.i18n';
import { TranscriptDialogService } from './transcript-dialog.service';
import { TranscriptDialogState } from './transcript-dialog.state';

@Component({
    selector: 'cmps-transcript-dialog',
    templateUrl: './transcript-dialog.component.html',
    host: {
        class: 'cmps-transcript-dialog',
    },
})
@TakeUntilDestroy()
export class TranscriptDialogComponent implements OnInit {
    /** @private */ _i18n = TranslationKeyMap;
    /** @private */ _View = View;

    constructor(
        private readonly _translateService: TranslateService,
        /** @private */ readonly _service: TranscriptDialogService,
        /** @private */ readonly _dialogRef: CmpsDialogRef<
            TranscriptDialogComponent,
            void
        >,
        @Inject(CMPS_DIALOG_DATA) /** @private */
        readonly _data: TranscriptDialogData,
        /** @private */ readonly _dialog: CmpsDialog,
        private readonly _switchboardService: CoachingSwitchboardService,
        private readonly _snackBar: CmpsSnackBar,
    ) {}

    ngOnInit(): void {
        useSelector2<boolean>(
            (state) =>
                (state?.coaching?.transcriptDialog as TranscriptDialogState)
                    ?.pending?.updateTranscript,
        )
            .pipe(
                skip(1),
                tap(([prevState, state]) => {
                    if (
                        prevState != null &&
                        state != null &&
                        prevState !== state &&
                        !state
                    ) {
                        this._transcriptUpdated();
                    }
                }),
                takeUntilDestroy(this),
            )
            .subscribe();

        useDispatcher(
            this._service,
            reload({
                platform: this._data.platform,
                transcriptId: this._data.transcriptId,
            }),
        );
    }

    /** @private */
    _onTitleChange(value: string): void {
        if (this._service.state?.transcript.name === value) {
            return;
        }
        useDispatcher(
            this._service,
            updateTranscript({
                transcript: {
                    ...this._service.state.transcript,
                    name: value,
                },
            }),
        );
    }

    /** @private */
    _onBackClick(): void {
        useDispatcher(this._service, reset({}));
        useDispatcher(this._service, setNoteViaSummary({}));
        useDispatcher(this._service, switchView({ view: View.LANDING }));
    }

    private _transcriptUpdated(): void {
        const data: CmpsTextOnlySnackBarOptions = {
            text: this._translateService.instant(
                TranslationKeyMap._10056_TRANSCRIPT_SAVED,
            ),
        };
        this._snackBar.open(CmpsTextOnlySnackBarComponent, {
            duration: 5000,
            placement: 'bottom-right',
            data,
        });

        this._switchboardService.transcriptSummary.sbReload();
    }
}
